import { formatAmountByDecimals } from '@/utils/numberFormatting';
import BigNumber from 'bignumber.js';
import { LayoutContext } from 'contexts/LayoutContext';
import * as d3Shape from 'd3-shape';
import { useContext, useMemo, useState } from 'react';
import { DonutChartData, DonutChartItem } from './DonutChart';

interface UseDonutChartParams {
  radius: number;
  data: Array<DonutChartItem['item']>;
  legendValueSuffix?: string;
}

export const useDonutChart = (props: UseDonutChartParams) => {
  const { radius, data, legendValueSuffix } = props;
  const layout = useContext(LayoutContext);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const parsedData = useMemo(() => {
    let sumValue = new BigNumber(0);
    let rawData: Array<DonutChartItem['item']> = [];
    let others: DonutChartItem['item'] = {
      name: 'Others',
      percentage: 0,
      value: '0'
    };

    data.forEach((item, index) => {
      const { name, percentage, value } = item;
      sumValue = sumValue.plus(new BigNumber(value));
      if (index < 3) {
        rawData.push({
          name,
          percentage: new BigNumber(percentage).toNumber(),
          value
        });
        return;
      }
      others.percentage = new BigNumber(others.percentage).plus(new BigNumber(percentage)).toNumber();
      others.value = new BigNumber(others.value).plus(new BigNumber(value))?.toString();
    });

    if (others.percentage > 0) {
      rawData.push(others);
    }

    return rawData;
  }, [data]);

  const pie = useMemo(() => {
    const values = parsedData
      .sort((a, b) => new BigNumber(b.percentage).toNumber() - new BigNumber(a.percentage).toNumber())
      .map((d) => new BigNumber(d.percentage).toNumber());
    return d3Shape.pie()(values);
  }, [data]);

  const arcs: DonutChartData = useMemo(() => {
    const innerRadiusRatio = layout.isMobile ? 0.5 : 0.5;
    const innerRadiusRatioSelected = layout.isMobile ? 0.53 : 0.53;

    return pie.map((item, i) => {
      const isSelected = selectedIndex === i;
      const obj = {
        innerRadius: isSelected ? radius * innerRadiusRatio : radius * innerRadiusRatioSelected,
        outerRadius: isSelected ? radius * 1 : radius * 0.95,
        startAngle: item.startAngle,
        endAngle: item.endAngle
      };
      return {
        textPosition: d3Shape.arc().centroid(obj) as [number, number],
        arc: d3Shape.arc()(obj),
        item: parsedData[i],
        selectArc: () => selectArc(i)
      };
    });
  }, [pie, selectedIndex, radius]);

  function selectArc(index: number) {
    setSelectedIndex(index);
  }

  const legendData = useMemo(() => {
    return parsedData.map((item) => ({
      name: item.name,
      percentage: item.percentage,
      data: [`${formatAmountByDecimals({ value: item.value, decimals: 2, suffix: legendValueSuffix })}`, `${item.percentage}%`]
    }));
  }, [parsedData]);

  return {
    legendData,
    chartData: arcs,
    selectArc
  };
};
